/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/components/AdaptToIndustryTab.tsx
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Select } from 'foundations-components/transitional/components/Select';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { useGetAllAvailableIndustriesQuery } from '../../../../__generated__/chirp/com/hubspot/blueprint/industry/rpc/IndustryRpc.hooks';
import CompanionFooter from './CompanionFooter.migrated';
import InteractiveContentError from './InteractiveContentError.migrated';
import InteractiveEducationLoading from './InteractiveEducationLoading.migrated';
import UsageTracker from '../../../utils/UsageTracker';
import { useUserIndustry } from '../hooks/useUserIndustry.migrated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SCROLL_CONTAINER_HEIGHT = '450px';
const AdaptToIndustryTab = ({
  activeIndustry,
  additionalTracking,
  contentId,
  contentType,
  videoTitle,
  useCase,
  useCaseLoading,
  useCaseError,
  correlationId,
  refetchUseCase,
  onGenerateForIndustry,
  isActive = false
}) => {
  var _availableIndustriesD2, _useCase$sections;
  const {
    data: availableIndustriesData
  } = useGetAllAvailableIndustriesQuery();
  const {
    industry: userSettingsIndustry,
    label: userSettingsIndustryLabel,
    loading: userSettingsIndustryLoading
  } = useUserIndustry();
  const [forceShowSelector, setForceShowSelector] = useState(false);
  const isIndustrySet = !!activeIndustry;
  const showSelector = forceShowSelector || !userSettingsIndustryLoading && !isIndustrySet;
  const hasTrackedGenerationRef = useRef(false);
  const handleIndustryChange = event => {
    var _event$target, _event$target2;
    const nextIndustry = event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value;
    setForceShowSelector(false);
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
      selectedIndustry: event === null || event === void 0 || (_event$target2 = event.target) === null || _event$target2 === void 0 ? void 0 : _event$target2.value,
      action: 'ai-adapt-to-industry-industry-selected'
    }));
    onGenerateForIndustry(nextIndustry);
  };
  const industryOptions = useMemo(() => {
    var _availableIndustriesD;
    return (availableIndustriesData === null || availableIndustriesData === void 0 || (_availableIndustriesD = availableIndustriesData.industries) === null || _availableIndustriesD === void 0 ? void 0 : _availableIndustriesD.filter(industryOption => readUnhandledEnumCase(industryOption.industry) !== 'OTHER').map(industryOption => ({
      text: industryOption.label,
      value: readUnhandledEnumCase(industryOption.industry)
    }))) || [];
  }, [availableIndustriesData === null || availableIndustriesData === void 0 ? void 0 : availableIndustriesData.industries]);
  const industryLabel = activeIndustry ? (availableIndustriesData === null || availableIndustriesData === void 0 || (_availableIndustriesD2 = availableIndustriesData.industries) === null || _availableIndustriesD2 === void 0 || (_availableIndustriesD2 = _availableIndustriesD2.find(industryOption => readUnhandledEnumCase(industryOption.industry) === activeIndustry)) === null || _availableIndustriesD2 === void 0 ? void 0 : _availableIndustriesD2.label) || userSettingsIndustryLabel : undefined;
  const getBadgeText = () => {
    if (!isIndustrySet) {
      return I18n.text('learningCenter.interactiveEducation.adaptToIndustry.defaultIndustryText');
    }
    return I18n.text('learningCenter.interactiveEducation.adaptToIndustry.industryBadgeText', {
      industry: industryLabel
    });
  };
  useEffect(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
      action: 'ai-adapt-to-industry-tab-rendered'
    }));
  }, [additionalTracking]);
  useEffect(() => {
    if (!activeIndustry && !userSettingsIndustryLoading) {
      UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
        action: 'ai-adapt-to-industry-select-ui-rendered'
      }));
    }
  }, [additionalTracking, activeIndustry, userSettingsIndustryLoading]);
  useEffect(() => {
    if (isActive && userSettingsIndustry && !userSettingsIndustryLoading && !useCase && !useCaseLoading && !useCaseError) {
      onGenerateForIndustry(userSettingsIndustry);
    }
  }, [isActive, userSettingsIndustry, userSettingsIndustryLoading, useCase, useCaseLoading, useCaseError, onGenerateForIndustry]);
  useEffect(() => {
    if (useCase && !hasTrackedGenerationRef.current) {
      var _activeIndustry$toStr;
      hasTrackedGenerationRef.current = true;
      UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
        selectedIndustry: (_activeIndustry$toStr = activeIndustry === null || activeIndustry === void 0 ? void 0 : activeIndustry.toString()) !== null && _activeIndustry$toStr !== void 0 ? _activeIndustry$toStr : '',
        action: 'ai-adapt-to-industry-generated-use-case'
      }));
    }
    if (!useCase) {
      hasTrackedGenerationRef.current = false;
    }
  }, [useCase, additionalTracking, activeIndustry]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      overflow: "hidden",
      paddingBlockStart: "700",
      paddingInline: "700",
      children: useCaseError ? /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: refetchUseCase,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.adaptToIndustry.title"
        })
      }) : useCaseLoading ? /*#__PURE__*/_jsx(InteractiveEducationLoading, {
        carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact1"
        }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact2"
        }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact3"
        }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact4"
        }, "fact4")],
        mainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.buildingIndustry"
        })
      }) : /*#__PURE__*/_jsxs(ScrollContainer, {
        blockSize: SCROLL_CONTAINER_HEIGHT,
        maxHeight: 450,
        paddingBlockEnd: "700",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "300",
          marginBlockEnd: "400",
          children: [/*#__PURE__*/_jsx(Badge, {
            use: "lorax",
            children: getBadgeText()
          }), isIndustrySet && !showSelector && /*#__PURE__*/_jsx(Link, {
            "data-test-id": "industry-change-link",
            onClick: () => setForceShowSelector(true),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.interactiveEducation.adaptToIndustry.changeIndustry"
            })
          })]
        }), userSettingsIndustryLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
          layout: "centered"
        }), showSelector && !userSettingsIndustryLoading && /*#__PURE__*/_jsxs(View, {
          "data-test-id": "industry-selection-container",
          marginBlockEnd: "600",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h3",
            color: "text-core-default",
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.interactiveEducation.adaptToIndustry.selectLabel"
            })
          }), /*#__PURE__*/_jsx(View, {
            maxInlineSize: "700px",
            children: /*#__PURE__*/_jsx(Select, {
              anchorType: "input",
              "data-test-id": "industry-selection-selector",
              onChange: handleIndustryChange,
              options: industryOptions,
              placeholder: I18n.text('learningCenter.interactiveEducation.adaptToIndustry.selectPlaceholder'),
              value: forceShowSelector ? '' : activeIndustry
            })
          })]
        }), isIndustrySet && videoTitle && !forceShowSelector && /*#__PURE__*/_jsxs(Text, {
          as: "h3",
          color: "text-core-default",
          marginBlockEnd: "400",
          children: [industryLabel, ": ", videoTitle]
        }), !forceShowSelector && (useCase === null || useCase === void 0 || (_useCase$sections = useCase.sections) === null || _useCase$sections === void 0 ? void 0 : _useCase$sections.map((section, index) => {
          var _section$bulletPoints;
          return /*#__PURE__*/_jsx(View, {
            "data-test-id": `industry-section-${index}`,
            paddingBlockEnd: "200",
            children: /*#__PURE__*/_jsxs(View, {
              marginBlockEnd: "200",
              paddingBlock: "200",
              children: [/*#__PURE__*/_jsx(View, {
                alignItems: "baseline",
                gap: "300",
                marginBlockEnd: "400",
                children: /*#__PURE__*/_jsx(Text, {
                  as: "h7",
                  color: "text-core-default",
                  children: section.sectionName
                })
              }), /*#__PURE__*/_jsx(List, {
                marginBlockStart: "200",
                paddingInlineStart: "800",
                styled: true,
                children: (_section$bulletPoints = section.bulletPoints) === null || _section$bulletPoints === void 0 ? void 0 : _section$bulletPoints.map((bullet, i) => /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(Text, {
                    color: "text-core-default",
                    children: bullet
                  })
                }, i))
              })]
            })
          }, section.sectionName);
        }))]
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      contentType: contentType,
      correlationId: correlationId,
      featureId: "ADAPT_TO_INDUSTRY",
      primaryContentId: contentId,
      trackingAction: "AI_ADAPT_TO_INDUSTRY_FEEDBACK"
    })]
  });
};
export default AdaptToIndustryTab;